import { useState } from "react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel, StatCard, DataTable } from "@/components/admin/AdminPrimitives";
import { useAnalyticsSummary, useArticleStats } from "@/hooks/useAnalytics";
import { useArticles, useStatistiquesAuteurs, useStatistiquesSponsorisation } from "@/hooks/useArticles";
import { Eye, BookOpen, Users, Sparkles } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/** Sélecteur d'intervalle [depuis, jusqu'au] réutilisé par les panneaux de statistiques ci-dessous. */
function IntervalPicker({
  depuis, jusqua, onDepuis, onJusqua,
}: { depuis: string; jusqua: string; onDepuis: (v: string) => void; onJusqua: (v: string) => void }) {
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <div className="space-y-1.5">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Depuis</label>
        <input
          type="date"
          value={depuis}
          onChange={(e) => onDepuis(e.target.value)}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
        />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Jusqu'au</label>
        <input
          type="date"
          value={jusqua}
          onChange={(e) => onJusqua(e.target.value)}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
        />
      </div>
    </div>
  );
}

function StatistiquesSponsorisation() {
  const [depuis, setDepuis] = useState("");
  const [jusqua, setJusqua] = useState("");
  const { data, isLoading } = useStatistiquesSponsorisation(
    depuis ? new Date(depuis).toISOString() : undefined,
    jusqua ? new Date(jusqua).toISOString() : undefined
  );

  return (
    <ModulePanel title="Articles sponsorisés" description="Répartition des articles publiés sponsorisés vs non sponsorisés">
      <IntervalPicker depuis={depuis} jusqua={jusqua} onDepuis={setDepuis} onJusqua={setJusqua} />
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Total publiés" value={isLoading ? "—" : data?.totalArticles ?? 0} icon={BookOpen} />
        <StatCard label="Sponsorisés" value={isLoading ? "—" : data?.articlesSponsorises ?? 0} icon={Sparkles} accent="verified" />
        <StatCard label="Non sponsorisés" value={isLoading ? "—" : data?.articlesNonSponsorises ?? 0} icon={BookOpen} />
      </div>
    </ModulePanel>
  );
}

const ROLE_LABEL: Record<string, string> = {
  REDACTEUR: "Rédacteur",
  PARTENAIRE: "Partenaire",
  ADMIN: "Admin",
  EDITEUR: "Éditeur",
  VERIFICATEUR: "Vérificateur",
  TRADUCTEUR: "Traducteur",
  LECTEUR: "Lecteur",
};

function PublicationsParAuteur() {
  const [depuis, setDepuis] = useState("");
  const [jusqua, setJusqua] = useState("");
  const { data, isLoading } = useStatistiquesAuteurs(
    depuis ? new Date(depuis).toISOString() : undefined,
    jusqua ? new Date(jusqua).toISOString() : undefined
  );
  const stats = Array.isArray(data) ? data : [];

  return (
    <ModulePanel title="Publications par auteur" description="Le nom du contributeur n'est visible que pour l'équipe éditoriale — un partenaire ne voit que son code">
      <IntervalPicker depuis={depuis} jusqua={jusqua} onDepuis={setDepuis} onJusqua={setJusqua} />
      {isLoading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Chargement…</div>
      ) : stats.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Aucun article publié sur cette période</div>
      ) : (
        <DataTable
          columns={["Auteur", "Code", "Rôle", "Articles publiés"]}
          rows={stats.map((s: any) => [
            s.auteurNom || "—",
            <span className="font-mono text-xs text-muted-foreground">{s.code || "—"}</span>,
            (s.roles || []).map((r: string) => ROLE_LABEL[r] || r).join(", ") || "—",
            s.nombreArticles,
          ])}
        />
      )}
    </ModulePanel>
  );
}

function TopContentViews({ articleId, locale }: { articleId: number; locale: string }) {
  const { data: stats, isLoading } = useArticleStats(articleId);
  if (isLoading) return <>—</>;
  return <>{(stats?.vues ?? 0).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}</>;
}

function TopContentReads({ articleId, locale }: { articleId: number; locale: string }) {
  const { data: stats, isLoading } = useArticleStats(articleId);
  if (isLoading) return <>—</>;
  return <>{(stats?.lectures ?? 0).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}</>;
}

function Analytics() {
  const { t, locale } = useI18n();
  const { data: summary, isLoading } = useAnalyticsSummary();
  const { data: articlesData } = useArticles(0, 20, "PUBLIE");
  const topArticles = Array.isArray(articlesData) ? articlesData : (articlesData as any)?.content ?? [];

  return (
    <>
      <AdminTopbar title={t("admin.analytics.title")} subtitle={t("admin.analytics.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            label={t("admin.dashboard.audience.views")}
            value={isLoading ? "—" : (summary?.vues ?? 0).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}
            icon={Eye}
          />
          <StatCard
            label={t("admin.dashboard.audience.reads")}
            value={isLoading ? "—" : (summary?.lectures ?? 0).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}
            icon={BookOpen}
            accent="verified"
          />
          <StatCard
            label={t("admin.dashboard.audience.visitors")}
            value={isLoading ? "—" : (summary?.visiteursUniques ?? 0).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}
            icon={Users}
          />
        </div>
        <ModulePanel title={t("admin.analytics.topContent")} description="Performance de chaque article publié">
          <DataTable
            columns={[t("admin.common.col.title"), "Sponsorisé", t("admin.analytics.col.views"), t("admin.dashboard.audience.reads")]}
            rows={topArticles.map((a: any) => [
              <span className="line-clamp-1 font-medium">{a.traductions?.[0]?.titre ?? a.slug}</span>,
              a.subventionne ? (
                <span className="inline-flex items-center gap-1 rounded-md bg-verified/10 px-1.5 py-0.5 text-[10px] font-semibold text-verified">
                  <Sparkles className="h-3 w-3" /> Sponsorisé
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              ),
              <TopContentViews articleId={a.id} locale={locale} />,
              <TopContentReads articleId={a.id} locale={locale} />,
            ])}
          />
        </ModulePanel>
        <StatistiquesSponsorisation />
        <PublicationsParAuteur />
      </div>
    </>
  );
}

export default Analytics;
